/**
 * Type scale and composed text roles.
 *
 * Components use roles (`textStyles.screenTitle`), never raw sizes, so the
 * master prompt's phone rules stay enforceable in one place:
 *   - the hero title must fit the phone width without clipping
 *   - footer typography must never exceed header typography
 * The second rule is asserted by a unit test in `theme.test.ts`.
 */

export const fontFamily = {
  /**
   * The artwork uses a geometric sans. Until the licensed family is supplied,
   * fall back to the platform UI font, which is metrically close enough not to
   * break layout. Override once font files land in `client-app/assets/fonts`.
   */
  sans: undefined as string | undefined,
  /** Transaction ids, references, and monetary columns in admin tables. */
  mono: undefined as string | undefined,
} as const;

export const fontWeight = {
  regular: '400',
  medium: '500',
  semibold: '600',
  bold: '700',
} as const;

export type FontWeight = (typeof fontWeight)[keyof typeof fontWeight];

/** Raw size ramp. Prefer a composed role below. */
export const fontSize = {
  xs: 11,
  sm: 13,
  base: 15,
  md: 16,
  lg: 18,
  xl: 20,
  '2xl': 24,
  '3xl': 28,
  '4xl': 34,
} as const;

export const lineHeight = {
  tight: 1.2,
  snug: 1.35,
  normal: 1.5,
  relaxed: 1.65,
} as const;

export const letterSpacing = {
  tight: -0.4,
  normal: 0,
  wide: 0.4,
  caps: 0.8,
} as const;

export interface TextStyle {
  fontSize: number;
  fontWeight: FontWeight;
  lineHeight: number;
  letterSpacing?: number;
  textTransform?: 'uppercase';
}

const style = (
  size: number,
  weight: FontWeight,
  lh: number,
  extra: Partial<TextStyle> = {},
): TextStyle => ({
  fontSize: size,
  fontWeight: weight,
  // Resolved to an absolute value: React Native rejects unitless multipliers.
  lineHeight: Math.round(size * lh),
  ...extra,
});

export const textStyles = {
  /** "A Meal from You, A Blessing for Them". Must wrap to two lines, never clip. */
  hero: style(fontSize['4xl'], fontWeight.bold, lineHeight.tight, {
    letterSpacing: letterSpacing.tight,
  }),
  /** Hero supporting sentence. */
  heroSubtitle: style(fontSize.md, fontWeight.regular, lineHeight.relaxed),

  /** "My Donations", "Payment", "Review Your Donation". */
  screenTitle: style(fontSize['3xl'], fontWeight.bold, lineHeight.tight),
  /**
   * The title inside a screen's green title card.
   *
   * A step below `screenTitle` on purpose: the card is a pinned strip of
   * chrome above the content, not a hero. At 28pt "Choose a Meal to Donate"
   * took two lines and a third of a phone screen before the first meal.
   */
  cardScreenTitle: style(fontSize.xl, fontWeight.bold, lineHeight.snug),
  /** The sentence under a screen title. */
  screenSubtitle: style(fontSize.base, fontWeight.regular, lineHeight.normal),

  /** "Donation Progress", "Overall Impact", "Saved Payment Methods". */
  sectionTitle: style(fontSize.lg, fontWeight.semibold, lineHeight.snug),
  /** Meal names, list row titles, notification titles. */
  cardTitle: style(fontSize.md, fontWeight.semibold, lineHeight.snug),

  /** Default body copy. */
  body: style(fontSize.base, fontWeight.regular, lineHeight.normal),
  /** Body copy that carries a value, e.g. a receipt row's right column. */
  bodyStrong: style(fontSize.base, fontWeight.semibold, lineHeight.normal),

  /** Field labels, list subtitles, card descriptions. */
  label: style(fontSize.sm, fontWeight.medium, lineHeight.normal),
  /** Timestamps, helper text, "Per Person". */
  caption: style(fontSize.sm, fontWeight.regular, lineHeight.normal),
  /**
   * A short line of detail under a small title, set close: three sit side by
   * side under Home's assurances, where a caption's spacing made each run to
   * three tall lines.
   */
  detail: style(fontSize.xs, fontWeight.regular, lineHeight.snug),
  /** A checkout step's name, stacked under its marker in a narrow column. */
  stepLabel: style(fontSize.xs, fontWeight.medium, lineHeight.snug),
  /** Status pills and small overlines. */
  badge: style(fontSize.xs, fontWeight.semibold, lineHeight.tight, {
    letterSpacing: letterSpacing.wide,
  }),

  /** Prices: "Rs. 16,000". */
  amount: style(fontSize.xl, fontWeight.bold, lineHeight.tight),
  /** Prices inside dense list rows. */
  amountSmall: style(fontSize.md, fontWeight.bold, lineHeight.tight),

  /** Button labels. */
  button: style(fontSize.md, fontWeight.semibold, lineHeight.tight),

  /**
   * Impact footer. Deliberately smaller than `brandName` below so the master
   * prompt's "footer type must not exceed header type" rule holds by
   * construction rather than by review.
   */
  footerValue: style(fontSize.md, fontWeight.bold, lineHeight.tight),
  footerCaption: style(fontSize.xs, fontWeight.regular, lineHeight.snug),

  /** Header brand lockup. */
  brandName: style(fontSize.lg, fontWeight.bold, lineHeight.tight),
  brandTagline: style(fontSize.xs, fontWeight.regular, lineHeight.snug),
} as const;

export type TextRole = keyof typeof textStyles;
